import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  ChakraSelect,
  FormControl,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
} from '@elkaso-app/web-design';
import { useGetSourceBrandLocationsApi } from 'apis/crm-source-brand-locations/use-get-source-brand-locations-api';
import { useSetParentSourceBrandLocationApi } from 'apis/crm-source-brand-locations/use-set-parent-source-brand-location-api';
import React, { useMemo, useState } from 'react';

interface SetParentLocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  locationId: number;
}

const SetParentLocationModal: React.FC<SetParentLocationModalProps> = ({ isOpen, onClose, locationId }) => {
  // API hooks
  const { data: locationsData, isLoading: isLoadingLocations } = useGetSourceBrandLocationsApi();
  const setParentMutation = useSetParentSourceBrandLocationApi();

  const [selectedParentId, setSelectedParentId] = useState<number | null>(null);

  // Find current location
  const currentLocation = locationsData?.data?.find((loc) => loc.id === locationId);

  // Filter out the current location, its children, and master locations from the available parents
  const availableParents =
    locationsData?.data?.filter(
      (loc) =>
        loc.id !== locationId && // Not the current location
        loc.parentId !== locationId && // Not a child of the current location
        !loc.isMaster // Master locations cannot be set as children
    ) || [];

  // Convert locations to ChakraSelect options
  const parentOptions = useMemo(() => {
    return availableParents.map((location) => ({
      value: location.id,
      label: `${location.name} - ${location.sourcePlatform} (${location.brand.name})${
        location.geoLocation?.name ? ` [${location.geoLocation.name}]` : ''
      }`,
    }));
  }, [availableParents]);

  // Find selected parent option
  const selectedParentOption = useMemo(() => {
    return selectedParentId ? parentOptions.find((option) => option.value === selectedParentId) : null;
  }, [selectedParentId, parentOptions]);

  const handleSubmit = async () => {
    try {
      await setParentMutation.mutateAsync({
        id: locationId,
        parentId: selectedParentId,
      });
      onClose();
    } catch (error) {
      // Error is handled in the API hook
    }
  };

  const handleRemoveParent = async () => {
    try {
      await setParentMutation.mutateAsync({
        id: locationId,
        parentId: null,
      });
      onClose();
    } catch (error) {
      // Error is handled in the API hook
    }
  };

  if (isLoadingLocations) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size='lg'>
        <ModalOverlay />
        <ModalContent>
          <ModalBody>
            <VStack spacing={4} py={8}>
              <Spinner size='lg' />
              <Text>Loading locations...</Text>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  if (!currentLocation) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size='md'>
        <ModalOverlay />
        <ModalContent>
          <ModalBody>
            <Alert status='error'>
              <AlertIcon />
              Location not found
            </Alert>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='lg'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Set Parent Location</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={4} align='stretch'>
            <Alert status='info'>
              <AlertIcon />
              Set a parent location to create a hierarchy. A location can only have one parent.
            </Alert>

            {/* Current Location Info */}
            <Box p={4} border='1px' borderColor='gray.200' borderRadius='md' bg='gray.50'>
              <Text fontWeight='bold' color='gray.700' mb={2}>
                Current Location
              </Text>
              <VStack align='start' spacing={1}>
                <Text fontWeight='medium'>{currentLocation.name}</Text>
                <HStack spacing={2}>
                  <Badge colorScheme='blue' variant='subtle' size='sm'>
                    {currentLocation.sourcePlatform}
                  </Badge>
                  <Text fontSize='sm' color='gray.600'>
                    Brand: {currentLocation.brand.name}
                  </Text>
                  {currentLocation.geoLocation?.name && (
                    <Badge colorScheme='green' variant='subtle' size='sm'>
                      {currentLocation.geoLocation.name}
                    </Badge>
                  )}
                </HStack>
                {currentLocation.parentId && (
                  <HStack spacing={2}>
                    <Text fontSize='sm' color='gray.600'>
                      Current Parent ID:
                    </Text>
                    <Badge colorScheme='orange' variant='subtle' size='sm'>
                      {currentLocation.parentId}
                    </Badge>
                  </HStack>
                )}
              </VStack>
            </Box>

            {/* Parent Selection */}
            <FormControl>
              <FormLabel>Select Parent Location</FormLabel>
              <ChakraSelect
                value={selectedParentOption}
                onChange={(selectedOption: any) => {
                  setSelectedParentId(selectedOption ? selectedOption.value : null);
                }}
                options={parentOptions}
                placeholder='Choose a parent location...'
                isClearable
                isSearchable
                getOptionLabel={(option: any) => option.label}
                getOptionValue={(option: any) => String(option.value)}
                closeMenuOnSelect={true}
                blurInputOnSelect={false}
                menuPlacement='auto'
                noOptionsMessage={() => 'No available parent locations'}
              />
            </FormControl>

            {selectedParentId && (
              <Box p={4} border='1px' borderColor='blue.200' borderRadius='md' bg='blue.50'>
                <Text fontWeight='bold' color='blue.700' mb={2}>
                  Selected Parent
                </Text>
                {(() => {
                  const selectedParent = availableParents.find((loc) => loc.id === selectedParentId);
                  return selectedParent ? (
                    <VStack align='start' spacing={1}>
                      <Text fontWeight='medium'>{selectedParent.name}</Text>
                      <HStack spacing={2}>
                        <Badge colorScheme='blue' variant='subtle' size='sm'>
                          {selectedParent.sourcePlatform}
                        </Badge>
                        <Text fontSize='sm' color='gray.600'>
                          Brand: {selectedParent.brand.name}
                        </Text>
                        {selectedParent.geoLocation?.name && (
                          <Badge colorScheme='green' variant='subtle' size='sm'>
                            {selectedParent.geoLocation.name}
                          </Badge>
                        )}
                      </HStack>
                      {selectedParent.address && (
                        <Text fontSize='sm' color='gray.600'>
                          {selectedParent.address}
                        </Text>
                      )}
                    </VStack>
                  ) : null;
                })()}
              </Box>
            )}

            {/* Warnings */}
            {currentLocation.parentId && (
              <Alert status='warning'>
                <AlertIcon />
                This location already has a parent. Setting a new parent will replace the current one.
              </Alert>
            )}

            {availableParents.length === 0 && (
              <Alert status='warning'>
                <AlertIcon />
                No other locations available to set as parent. Master locations cannot be set as children of other
                locations.
              </Alert>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={2}>
            {currentLocation.parentId && (
              <Button
                colorScheme='orange'
                variant='outline'
                onClick={handleRemoveParent}
                isLoading={setParentMutation.isLoading}
                loadingText='Removing...'>
                Remove Parent
              </Button>
            )}
            <Button variant='ghost' mr={3} onClick={onClose} disabled={setParentMutation.isLoading}>
              Cancel
            </Button>
            <Button
              colorScheme='blue'
              onClick={handleSubmit}
              isLoading={setParentMutation.isLoading}
              loadingText='Setting...'
              disabled={!selectedParentId}>
              Set Parent
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SetParentLocationModal;
